// src/hooks/useViewportInsets.js
//
// Mantem atualizadas as variaveis CSS --vvb (visual viewport bottom) e
// --vvh (visual viewport height) sempre que o teclado virtual abre/fecha
// ou a janela redimensiona. iOS Safari e Android Chrome encolhem o
// visualViewport sem alterar window.innerHeight, deixando posicoes fixas
// escondidas atras do teclado.
//
// Uso:
//   useViewportInsets();
//   /* CSS */ padding-bottom: calc(var(--vvb, 0px) + env(safe-area-inset-bottom, 0px));

import { useEffect } from 'react';

export default function useViewportInsets() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let rafId = null;
    let lastBottom = -1;
    let lastHeight = -1;

    const update = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const vv = window.visualViewport;
        const innerH = window.innerHeight || 0;
        const vvHeight = vv ? vv.height : innerH;
        const vvOffsetTop = vv ? vv.offsetTop : 0;

        const bottomInset = Math.max(0, innerH - vvHeight - vvOffsetTop);
        const roundedBottom = Math.ceil(bottomInset);
        const roundedHeight = Math.ceil(vvHeight);

        if (roundedBottom !== lastBottom) {
          lastBottom = roundedBottom;
          document.documentElement.style.setProperty('--vvb', `${roundedBottom}px`);
        }
        if (roundedHeight !== lastHeight) {
          lastHeight = roundedHeight;
          document.documentElement.style.setProperty('--vvh', `${roundedHeight}px`);
        }
      });
    };

    update();

    window.visualViewport?.addEventListener('resize', update, { passive: true });
    window.visualViewport?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
}
